import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { Box, Paper, Typography, Button, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

/** Right-side preview panel width (px); same pattern as ARN Insight / Offline CUR manual. */
export const REMEDIATION_PREVIEW_PANEL_WIDTH_PX = 600;

const COLORS = {
  background: "#fafafa",
  card: "#ffffff",
  border: "#e4e4e7",
  borderLight: "#f4f4f5",
  text: "#18181b",
  textMuted: "#71717a",
};

export const isHttpUrlString = (v) =>
  typeof v === "string" && /^https?:\/\//i.test(v.trim());

/**
 * Shared open/close state for in-app remediation library preview.
 * Render `panel` once near the component root.
 */
export function useRemediationPreview() {
  const [url, setUrl] = useState(null);

  const open = useCallback((nextUrl) => {
    if (isHttpUrlString(nextUrl)) setUrl(String(nextUrl).trim());
    else if (nextUrl) window.open(String(nextUrl), "_blank", "noopener,noreferrer");
  }, []);

  const close = useCallback(() => {
    setUrl(null);
  }, []);

  return {
    url,
    open,
    close,
    panel: <RemediationPreviewPanel url={url} onClose={close} />,
  };
}

/**
 * In-app remediation document preview (portaled side panel + iframe).
 * Matches ARN Insight “View remediation” behavior.
 */
export default function RemediationPreviewPanel({ url, onClose }) {
  useEffect(() => {
    if (!url) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose?.();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [url, onClose]);

  if (!url || !isHttpUrlString(url)) return null;

  const trimmed = String(url).trim();

  return createPortal(
    <Paper
      component="aside"
      elevation={8}
      aria-label="Remediation document preview"
      sx={{
        position: "fixed",
        right: 0,
        top: 0,
        bottom: 0,
        left: { xs: 0, md: "auto" },
        width: { xs: "100vw", md: `${REMEDIATION_PREVIEW_PANEL_WIDTH_PX}px` },
        minWidth: 0,
        maxWidth: "100vw",
        maxHeight: "100dvh",
        zIndex: 1400,
        borderRadius: 0,
        border: `1px solid ${COLORS.border}`,
        borderTop: 0,
        borderBottom: 0,
        borderRight: 0,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        boxSizing: "border-box",
        backgroundColor: COLORS.card,
      }}
    >
      <Box
        component="header"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          minHeight: 44,
          px: 1.5,
          py: 0.75,
          backgroundColor: COLORS.borderLight,
          borderBottom: `1px solid ${COLORS.border}`,
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{
            fontSize: "0.875rem",
            fontWeight: 600,
            color: COLORS.text,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={trimmed}
        >
          Remediation
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexShrink: 0 }}>
          <Button
            component="a"
            size="small"
            color="primary"
            variant="outlined"
            startIcon={<OpenInNewIcon sx={{ fontSize: 18 }} />}
            href={trimmed}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.75rem",
            }}
          >
            Open in a New Tab
          </Button>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label="Close preview"
            sx={{ color: COLORS.textMuted, "&:hover": { color: COLORS.text } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
      <Box
        sx={{
          position: "relative",
          flex: "1 1 0%",
          minHeight: 0,
          minWidth: 0,
          width: "100%",
          overflow: "visible",
        }}
      >
        <Box
          component="iframe"
          key={trimmed}
          title="Remediation document"
          src={trimmed}
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            border: 0,
            display: "block",
            backgroundColor: COLORS.background,
          }}
        />
      </Box>
    </Paper>,
    document.body
  );
}
